import { useEffect, useState } from 'react';
import { addDaysToLocalDate, localDateAt, localDateTimeToInstant, progress, type DayMark, type Progress as P } from '@drbell/core';
import { api, type PatientSummary, type TimelineDose } from '../api.ts';

/**
 * Progress — docs/33 C1, parity with the phone.
 *
 * Describes, never grades: per medicine, the last seven days; the month, day
 * by day; and plain counts. One colour, filled / half / outline, no
 * percentage, no colour that means good or bad. The numbers are core's
 * `progress()`, the same the phone draws.
 */
const WORD: Record<DayMark, string> = {
  all: 'every dose answered',
  some: 'some answered',
  none: 'none answered',
  empty: 'nothing due',
  ahead: 'still to come',
};

function Mark({ mark, size, label }: { mark: DayMark; size: number; label?: string }) {
  const r = size / 2 - 2;
  const c = size / 2;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true}>
      {mark === 'all' && <circle cx={c} cy={c} r={r} fill="var(--accent)" />}
      {mark === 'some' && (
        <>
          <circle cx={c} cy={c} r={r} fill="none" stroke="var(--accent)" strokeWidth={2} />
          <path d={`M ${c} ${c - r} A ${r} ${r} 0 0 1 ${c} ${c + r} Z`} fill="var(--accent)" />
        </>
      )}
      {mark === 'none' && <circle cx={c} cy={c} r={r} fill="none" stroke="var(--accent)" strokeWidth={2} />}
      {mark === 'ahead' && <circle cx={c} cy={c} r={r} fill="none" stroke="var(--line)" strokeWidth={2} strokeDasharray="3 3" />}
      {mark === 'empty' && <circle cx={c} cy={c} r={2} fill="var(--line)" />}
    </svg>
  );
}

export function Progress({ patient, onHistory }: { patient: PatientSummary; onHistory: () => void }) {
  const [data, setData] = useState<P | null>(null);
  const [error, setError] = useState<string | null>(null);
  const tz = patient.timezone;

  useEffect(() => {
    let live = true;
    const now = Date.now();
    const today = localDateAt(now, tz);
    const monthStart = `${today.slice(0, 7)}-01`;
    const weekStart = addDaysToLocalDate(today, -6);
    const from = localDateTimeToInstant(monthStart < weekStart ? monthStart : weekStart, '00:00', tz);
    const to = localDateTimeToInstant(addDaysToLocalDate(today, 1), '00:00', tz) - 1;
    api.get<{ doses: TimelineDose[] }>(`/api/patients/${patient.id}/doses?from=${from}&to=${to}`)
      .then((r) => {
        if (!live) return;
        setData(progress(
          r.doses.map((d) => ({
            medicationId: d.dose.medicationId ?? d.medication.name,
            medicationName: d.medication.name,
            dueAt: d.dose.dueAt,
            windowEnd: d.dose.windowEnd,
            status: d.dose.status,
          })),
          today, tz, now,
        ));
      })
      .catch((e) => live && setError(String(e)));
    return () => { live = false; };
  }, [patient.id, tz]);

  if (error) return <p role="alert" className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-500">{error}</p>;
  if (!data) return <p className="text-(--muted)">Loading…</p>;

  const weekday = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString('en-IN', { weekday: 'short', timeZone: 'UTC' });
  const monthName = new Date(`${data.month[0]!.date}T12:00:00Z`).toLocaleDateString('en-IN', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  // Monday first: blanks before the 1st.
  const dow = new Date(`${data.month[0]!.date}T12:00:00Z`).getUTCDay();
  const blanks = dow === 0 ? 6 : dow - 1;

  return (
    <section className="space-y-6">
      <p className="text-sm text-(--muted)">
        What became of each dose: filled when every dose of the day was answered, half when some were, an outline when
        none were.
      </p>

      <div>
        <h2 className="mb-2 text-sm font-medium">The last seven days</h2>
        {data.medicines.length === 0 ? (
          <p className="text-sm text-(--muted)">No doses in these seven days.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="text-left font-normal text-(--muted)"><span className="sr-only">Medicine</span></th>
                {data.week.map((d) => <th key={d} className="w-10 text-center text-xs font-normal text-(--muted)">{weekday(d)}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.medicines.map((m) => (
                <tr key={m.medicationId} className="border-t border-(--line)">
                  <td className="py-2 pr-2">{m.name}</td>
                  {m.days.map((mark, i) => (
                    <td key={data.week[i]} className="text-center">
                      <span className="inline-flex"><Mark mark={mark} size={22} label={`${m.name}, ${data.week[i]}: ${WORD[mark]}`} /></span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div>
        <h2 className="mb-2 text-sm font-medium">{monthName}</h2>
        <div className="grid max-w-md grid-cols-7 gap-y-2 text-center">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => <span key={d} className="text-xs text-(--muted)">{d}</span>)}
          {Array.from({ length: blanks }, (_, i) => <span key={`b${i}`} />)}
          {data.month.map((cell) => (
            <span key={cell.date} className="flex flex-col items-center gap-0.5">
              <span className="text-xs text-(--muted)">{Number(cell.date.slice(8))}</span>
              <Mark mark={cell.mark} size={22} label={`${cell.date}: ${WORD[cell.mark]}`} />
            </span>
          ))}
        </div>
        <ul className="mt-3 space-y-1 text-xs text-(--muted)">
          {(['all', 'some', 'none'] as const).map((k) => (
            <li key={k} className="flex items-center gap-2"><Mark mark={k} size={16} />{WORD[k]}</li>
          ))}
        </ul>
      </div>

      <p className="text-sm">
        This month: {data.counts.taken} taken, {data.counts.skipped} skipped, {data.counts.missed} missed, of {data.counts.due} due so far.
      </p>

      <button onClick={onHistory} className="min-h-11 rounded-lg border border-(--line) px-4 text-sm hover:border-(--accent)">
        History and what to show the doctor
      </button>
    </section>
  );
}
